import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Accordion } from 'src/design/molecules/Accordion';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Fieldset } from 'src/design/atoms/Fieldset';
import { Input } from 'src/design/atoms/Input';
import { TextInput } from 'src/design/atoms/TextInput';
import { IconSettings } from '@tabler/icons-react';
import React from 'react';
import { WorkflowDto } from 'src/api/FiretrolApiClient';
import { ColorSelect, IntentType } from 'src/app/selectors/ColorSelect';
import { JobUserSelectV2 } from 'src/app/selectors/JobUserSelectV2';
import { RoleUserSelector } from './RoleUserSelector';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { useOrganization } from 'src/hooks/useOrganization';
import { OfficeSelector } from 'src/app/selectors/OfficeSelectorSingle';

export interface IWorkflowSettingsProps {
  settings: WorkflowDto;
  onChange: (key: string, value: any) => void;
  isTemplateEditor: boolean;
  deleteWorkflow?: () => Promise<void>;
  onNavigate: () => void;
  readOnly?: boolean;
}

/** @registry Workflow-level settings panel — office, name, active flag, and initiation-authorization controls. */
export const WorkflowSettings: React.FC<IWorkflowSettingsProps> = ({
  settings,
  onChange,
  isTemplateEditor,
  deleteWorkflow,
  onNavigate,
  readOnly = false,
}) => {
  const { loading } = useOrganization();

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Stack>
          <Group gap="sm" justify="flex-start">
            <SecondaryButton onClick={onNavigate}>Back</SecondaryButton>

            {deleteWorkflow && !readOnly && (
              <InlineConfirmButton
                color="red"
                confirmButtonProps={{
                  children: 'Confirm Delete',
                }}
                onConfirmClick={deleteWorkflow}
              >
                Delete Workflow
              </InlineConfirmButton>
            )}
          </Group>

          <Fieldset disabled={readOnly} variant="unstyled" p={0}>
            <Stack>
              <Input.Wrapper
                label="Office"
                description="To which office does this workflow belong?"
              >
                {!loading ? (
                  <OfficeSelector
                    initialOfficeId={settings.officeId}
                    initialValue={''}
                    onChange={() => {}}
                    onOfficeChange={(office) =>
                      onChange('officeId', office ? office.id : null)
                    }
                  />
                ) : (
                  <Text size="sm" c="dimmed">
                    Loading offices...
                  </Text>
                )}
              </Input.Wrapper>

              <TextInput
                label="Workflow Name"
                description="What do you want to call this workflow?"
                value={settings.name}
                onChange={(e) => onChange('name', e.target.value)}
              />

              {isTemplateEditor && (
                <Checkbox
                  label="Active"
                  description="Allow your organization to use this workflow?"
                  checked={settings.isActive}
                  onChange={(e) => onChange('isActive', e.target.checked)}
                />
              )}
            </Stack>
          </Fieldset>
        </Stack>
      </Paper>

      <Paper withBorder p={0}>
        <Accordion variant="default" color="white">
          <Accordion.Item value="workflow-settings" style={{ border: 'none' }}>
            <Accordion.Control>
              <Group gap="xs">
                <IconSettings size={18} color="var(--mantine-color-dark-9)" />
                <Text c="bright">Workflow Settings</Text>{' '}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Fieldset disabled={readOnly} variant="unstyled" p={0}>
                <Stack>
                  <Group grow>
                    <TextInput
                      label="Create Button Text"
                      description="Text the user will see to initiate workflow"
                      value={settings.createButtonText}
                      onChange={(e) =>
                        onChange('createButtonText', e.target.value)
                      }
                    />

                    <Input.Wrapper
                      label="Button Color"
                      description="Color of the create button"
                    >
                      <ColorSelect
                        onChange={(value) =>
                          onChange('createButtonColor', value)
                        }
                        value={
                          (settings.createButtonColor as IntentType) ??
                          'primary'
                        }
                        hideLabel
                      />
                    </Input.Wrapper>
                  </Group>

                  <Checkbox
                    checked={settings.allowDraft}
                    label="Allow Drafts"
                    onChange={(e) => onChange('allowDraft', e.target.checked)}
                  />

                  <Checkbox
                    checked={settings.allowOwner}
                    label="Allow Owner"
                    description="Resource owners can initiate this workflow for their own items"
                    onChange={(e) => onChange('allowOwner', e.target.checked)}
                  />

                  <Input.Wrapper
                    label="Authorized Roles"
                    description="Users with selected roles will be allowed to initiate this workflow"
                  >
                    <RoleUserSelector
                      placeholder="Select authorized roles..."
                      onFieldChange={(field, value) =>
                        onChange(
                          field,
                          value.map((role) => ({
                            organizationRoleId: role.id,
                            organizationRole: role,
                          })),
                        )
                      }
                      initialUsers={settings.excludedUsers.map(
                        (user) => user.jobUser,
                      )}
                      onUserSelect={(users) =>
                        onChange(
                          'excludedUsers',
                          users.map((user) => ({
                            jobUser: user,
                            jobUserId: user.id,
                          })),
                        )
                      }
                      initialSelectedRoles={settings.authorizedRoles.map(
                        (role) => role.organizationRole,
                      )}
                      selectDescription="Select users to EXCLUDE from authorized roles!"
                      selectedRoles={settings.authorizedRoles.map(
                        (role) => role.organizationRole,
                      )}
                      field={'authorizedRoles'}
                    />
                  </Input.Wrapper>

                  {!isTemplateEditor && (
                    <Input.Wrapper
                      label="Authorized Users"
                      description="Selected users will also be allowed to transition to this status regardless of roles"
                    >
                      <JobUserSelectV2
                        onChange={(value) =>
                          onChange(
                            'authorizedUsers',
                            value.map((user) => ({
                              jobUser: user,
                              jobUserId: user.id,
                            })),
                          )
                        }
                        value={settings.authorizedUsers.map(
                          (user) => user.jobUser,
                        )}
                        hideLabel
                      />
                    </Input.Wrapper>
                  )}
                </Stack>
              </Fieldset>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Paper>
    </Stack>
  );
};
